import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { IconChevronLeft, IconChevronRight, IconX } from "@tabler/icons-react";

import { Button, Text } from "@fragments";

import "./styles.scss";

const CHROME_IDLE_MS = 2000;

/**
 * Slide-deck presentation of a pdf.js document. Runs fullscreen by design:
 * the overlay enters fullscreen on mount, and leaving fullscreen (Esc or the
 * browser UI) ends the presentation.
 *
 * Keys: → ↓ Space PageDown Enter = next · ← ↑ PageUp Backspace = previous
 *       Home / End = first / last · Esc = exit
 * Click / tap on the slide reveals the controls for 2s.
 */
export default function Presentation({ pdf, initialPage = 1, title, onClose, onPageChange }) {
    const { t } = useTranslation("content");
    const numPages = pdf?.numPages || 0;

    const [page, setPage] = useState(() => clamp(initialPage, 1, numPages || 1));
    const [rendering, setRendering] = useState(true);
    const [chromeVisible, setChromeVisible] = useState(false);

    const rootRef = useRef(null);
    const stageRef = useRef(null);
    const slideRef = useRef(null);
    const cacheRef = useRef(new Map()); // `${page}@${w}x${h}` -> canvas
    const stageSizeRef = useRef({ width: 0, height: 0 });
    const idleTimerRef = useRef(null);
    const enteredFullscreenRef = useRef(false);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const goTo = useCallback((target) => {
        setPage((current) => {
            const next = clamp(target, 1, numPages || 1);
            return next === current ? current : next;
        });
    }, [numPages]);

    const goNext = useCallback(() => goTo(page + 1), [goTo, page]);
    const goPrev = useCallback(() => goTo(page - 1), [goTo, page]);

    // --- Fullscreen by design ----------------------------------------------
    useEffect(() => {
        const root = rootRef.current;
        if (!root) return undefined;

        let cancelled = false;

        // The mount follows the "Apresentar" click, so this still counts as a
        // user gesture. If the browser refuses, fall back to the fixed overlay.
        root.requestFullscreen?.()
            .then(() => {
                if (!cancelled) enteredFullscreenRef.current = true;
            })
            .catch(() => {});

        const handleFullscreenChange = () => {
            if (enteredFullscreenRef.current && !document.fullscreenElement) {
                onCloseRef.current?.();
            }
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);

        return () => {
            cancelled = true;
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            if (document.fullscreenElement === root) document.exitFullscreen?.();
        };
    }, []);

    // --- Keyboard ------------------------------------------------------------
    useEffect(() => {
        function handleKeyDown(event) {
            if (event.metaKey || event.ctrlKey || event.altKey) return;

            switch (event.key) {
                case "ArrowRight":
                case "ArrowDown":
                case "PageDown":
                case "Enter":
                case " ":
                    event.preventDefault();
                    goNext();
                    break;
                case "ArrowLeft":
                case "ArrowUp":
                case "PageUp":
                case "Backspace":
                    event.preventDefault();
                    goPrev();
                    break;
                case "Home":
                    event.preventDefault();
                    goTo(1);
                    break;
                case "End":
                    event.preventDefault();
                    goTo(numPages);
                    break;
                case "Escape":
                    // In fullscreen the browser exits fullscreen on Esc, which
                    // closes us via fullscreenchange; outside it, close directly.
                    if (!document.fullscreenElement) {
                        event.preventDefault();
                        onCloseRef.current?.();
                    }
                    break;
                default:
            }
        }

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [goNext, goPrev, goTo, numPages]);

    // --- Controls: revealed on click, dismissed after 2s of mouse inactivity --
    const hoveringControlsRef = useRef(false);

    const armIdleTimer = useCallback(() => {
        window.clearTimeout(idleTimerRef.current);
        // Never count down while the pointer rests on a control region.
        if (hoveringControlsRef.current) return;
        idleTimerRef.current = window.setTimeout(() => setChromeVisible(false), CHROME_IDLE_MS);
    }, []);

    const revealChrome = useCallback(() => {
        setChromeVisible(true);
        armIdleTimer();
    }, [armIdleTimer]);

    // While the controls are visible, any mouse movement counts as activity
    // and pushes the dismissal back; they only fade after 2s of stillness.
    function handleMouseActivity() {
        if (chromeVisible) armIdleTimer();
    }

    // Hovering the header, footer or nav buttons holds the controls open;
    // the countdown restarts once the pointer leaves that region.
    function handleControlsEnter() {
        hoveringControlsRef.current = true;
        window.clearTimeout(idleTimerRef.current);
    }

    function handleControlsLeave() {
        hoveringControlsRef.current = false;
        if (chromeVisible) armIdleTimer();
    }

    const holdProps = { onMouseEnter: handleControlsEnter, onMouseLeave: handleControlsLeave };

    useEffect(() => () => window.clearTimeout(idleTimerRef.current), []);

    function handleStageClick() {
        revealChrome();
    }

    // --- Lock page scroll while presenting -----------------------------------
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previous;
        };
    }, []);

    // --- Report page changes to the parent -----------------------------------
    useEffect(() => {
        onPageChange?.(page);
    }, [page, onPageChange]);

    // --- Render the current page (and pre-warm neighbours) -------------------
    const renderPageToCanvas = useCallback(async (pageNum, { width, height }) => {
        const key = `${pageNum}@${width}x${height}`;
        if (cacheRef.current.has(key)) return cacheRef.current.get(key);

        const pdfPage = await pdf.getPage(pageNum);
        const base = pdfPage.getViewport({ scale: 1 });
        const scale = Math.min(width / base.width, height / base.height);
        const viewport = pdfPage.getViewport({ scale });

        const dpr = window.devicePixelRatio || 1;
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const context = canvas.getContext("2d");
        context.setTransform(dpr, 0, 0, dpr, 0, 0);
        await pdfPage.render({ canvasContext: context, viewport }).promise;

        cacheRef.current.set(key, canvas);
        return canvas;
    }, [pdf]);

    useEffect(() => {
        if (!pdf || !stageRef.current) return undefined;

        let cancelled = false;
        const stage = stageRef.current;

        const measure = () => ({
            width: Math.max(stage.clientWidth, 1),
            height: Math.max(stage.clientHeight, 1),
        });

        async function draw() {
            const size = measure();
            stageSizeRef.current = size;
            setRendering(true);

            try {
                const canvas = await renderPageToCanvas(page, size);
                if (cancelled || !slideRef.current) return;
                slideRef.current.replaceChildren(canvas);
                setRendering(false);

                // Pre-warm neighbours so arrow presses feel instant.
                [page + 1, page - 1]
                    .filter((n) => n >= 1 && n <= numPages)
                    .forEach((n) => renderPageToCanvas(n, size).catch(() => {}));
            } catch (err) {
                console.error(`Error presenting page ${page}:`, err);
                if (!cancelled) setRendering(false);
            }
        }

        draw();

        // Re-fit when the stage changes size (e.g. the fullscreen transition).
        const resizeObserver = new ResizeObserver(() => {
            const size = measure();
            const prev = stageSizeRef.current;
            if (size.width === prev.width && size.height === prev.height) return;
            cacheRef.current.clear();
            draw();
        });
        resizeObserver.observe(stage);

        return () => {
            cancelled = true;
            resizeObserver.disconnect();
        };
    }, [pdf, page, numPages, renderPageToCanvas]);

    if (!pdf) return null;

    const progress = numPages ? (page / numPages) * 100 : 0;
    const focusable = chromeVisible ? 0 : -1;

    return createPortal(
        <div
            ref={rootRef}
            className={`material-presentation ${chromeVisible ? "material-presentation--controls" : ""}`.trim()}
            role="dialog"
            aria-modal="true"
            aria-label={title ? t("presentation.ariaWithTitle", { title }) : t("presentation.aria")}
            onMouseMove={handleMouseActivity}
            onTouchStart={handleMouseActivity}
        >
            <div
                ref={stageRef}
                className="material-presentation__stage"
                onClick={handleStageClick}
                onContextMenu={(e) => e.preventDefault()}
            >
                <div
                    ref={slideRef}
                    className={`material-presentation__slide ${rendering ? "material-presentation__slide--rendering" : ""}`.trim()}
                    key={page}
                />
            </div>

            <div className="material-presentation__chrome" aria-hidden={!chromeVisible}>
                <header className="material-presentation__bar material-presentation__bar--top" {...holdProps}>
                    <Text.span contrast={8} weight={500} maxLines={1} grow>
                        {title}
                    </Text.span>
                    <Button
                        variant="rounded"
                        color="overlay"
                        size="small"
                        icon={<IconX />}
                        tooltip={t("presentation.close")}
                        aria-label={t("presentation.closeLabel")}
                        tabIndex={focusable}
                        onClick={onClose}
                    />
                </header>

                <div className="material-presentation__nav material-presentation__nav--prev" {...holdProps}>
                    <Button
                        variant="rounded"
                        color="overlay"
                        icon={<IconChevronLeft />}
                        aria-label={t("presentation.prevSlide")}
                        disabled={page <= 1}
                        tabIndex={focusable}
                        onClick={goPrev}
                    />
                </div>
                <div className="material-presentation__nav material-presentation__nav--next" {...holdProps}>
                    <Button
                        variant="rounded"
                        color="overlay"
                        icon={<IconChevronRight />}
                        aria-label={t("presentation.nextSlide")}
                        disabled={page >= numPages}
                        tabIndex={focusable}
                        onClick={goNext}
                    />
                </div>

                <footer className="material-presentation__bar material-presentation__bar--bottom" {...holdProps}>
                    <Text.caption contrast={6}>
                        {t("presentation.hint")}
                    </Text.caption>
                    <Text.span contrast={8} weight={500} className="material-presentation__counter">
                        {page} / {numPages}
                    </Text.span>
                </footer>

                <div className="material-presentation__progress">
                    <span style={{ width: `${progress}%` }} />
                </div>
            </div>
        </div>,
        document.body,
    );
}

function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
